// Hook to fetch and manage expert dossiers

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { expertAPI } from '../api/expert.api';
import { parseDate } from '@/core/utils/date.utils';
import type { DossierFilters } from '../types/expert.types';

export const useExpertDossiers = (expertFmName: string | null) => {
  const [filters, setFilters] = useState<DossierFilters>({
    searchQuery: '',
    sortBy: 'date',
    sortOrder: 'desc',
    activeFilter: 'all'
  });

  // Fetch dossiers for selected expert
  const {
    data: dossiersData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['admin-expert-dossiers', expertFmName],
    queryFn: () => expertFmName ? expertAPI.getExpertDossiers(expertFmName) : Promise.resolve(null),
    enabled: !!expertFmName,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Fetch stats for selected expert
  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError
  } = useQuery({
    queryKey: ['admin-expert-stats', expertFmName],
    queryFn: () => expertFmName ? expertAPI.getExpertStats(expertFmName) : Promise.resolve(null),
    enabled: !!expertFmName,
    staleTime: 60 * 1000, // 1 minute
  });

  // Process dossiers to add urgency information
  const processedDossiers = useMemo(() => {
    if (!dossiersData?.data?.expertises) return [];

    return dossiersData.data.expertises.map(dossier => {
      let isUrgent = false;
      let urgencyReason = '';
      let daysSinceRv: number | null = null;

      if (dossier.date_du_rv) {
        const rvDate = parseDate(dossier.date_du_rv);
        if (rvDate) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          rvDate.setHours(0, 0, 0, 0);
          
          daysSinceRv = Math.floor((today.getTime() - rvDate.getTime()) / (1000 * 60 * 60 * 24));
          
          const isPaid = dossier.perception === 'PAYE BANQUE' || 
                        dossier.perception?.toLowerCase().includes('paye') ||
                        dossier.perception?.toLowerCase().includes('payé');
          
          if (daysSinceRv > 2 && !isPaid) {
            isUrgent = true;
            urgencyReason = `${daysSinceRv} jours depuis la visite`;
          }
        }
      }

      // Calculate time since creation
      const timeSince = dossier.date ? (() => {
        const creationDate = parseDate(dossier.date);
        if (!creationDate) return null;
        const days = Math.floor((new Date().getTime() - creationDate.getTime()) / (1000 * 60 * 60 * 24));
        if (days === 0) return "Aujourd'hui";
        if (days === 1) return "1 jour";
        if (days < 7) return `${days} jours`;
        if (days < 30) return `${Math.floor(days / 7)} sem`;
        return `${Math.floor(days / 30)} mois`;
      })() : null;

      return {
        ...dossier,
        isUrgent,
        urgencyReason,
        daysSinceRv,
        timeSince
      };
    });
  }, [dossiersData]);

  // Filter and sort dossiers
  const filteredDossiers = useMemo(() => {
    const filtered = processedDossiers.filter(dossier => {
      // Apply search filter
      if (filters.searchQuery.trim()) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch = 
          dossier.type?.toLowerCase().includes(query) ||
          dossier.id?.toString().includes(query) ||
          dossier.status?.toLowerCase().includes(query) ||
          dossier.expert?.toLowerCase().includes(query) ||
          dossier.address?.some(addr => addr.toLowerCase().includes(query)) ||
          dossier.client?.name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Apply status filter
      if (filters.activeFilter !== 'all') {
        const isPaid = dossier.perception === 'PAYE BANQUE' || 
                       dossier.perception?.toLowerCase().includes('preuve virement');
        
        switch (filters.activeFilter) {
          case 'urgent':
            return dossier.isUrgent;
          case 'paid':
            return isPaid;
          case 'unpaid':
            return !isPaid;
          default:
            return true;
        }
      }
      
      return true;
    });
    
    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'date': {
          const dateA = parseDate(a.date);
          const dateB = parseDate(b.date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          comparison = dateB.getTime() - dateA.getTime();
          break;
        }

        case 'date_rv': {
          const rvDateA = parseDate(a.date_du_rv);
          const rvDateB = parseDate(b.date_du_rv);
          if (!rvDateA && !rvDateB) return 0;
          if (!rvDateA) return 1;
          if (!rvDateB) return -1;
          comparison = rvDateB.getTime() - rvDateA.getTime();
          break;
        }

        case 'id':
          comparison = (b.id || 0) - (a.id || 0);
          break;

        case 'status': {
          const statusA = a.status || '';
          const statusB = b.status || '';
          comparison = statusA.localeCompare(statusB);
          break;
        }

        default:
          return 0;
      }
      
      // Apply sort order
      return filters.sortOrder === 'asc' ? -comparison : comparison;
    });
    
    return sorted;
  }, [processedDossiers, filters]);

  return {
    dossiers: filteredDossiers,
    expertName: dossiersData?.data?.expertName || '',
    expertFmName: dossiersData?.data?.expertFmName || '',
    total: dossiersData?.data?.total || 0,
    stats: statsData?.data?.stats || null,
    isLoading: isLoading || statsLoading,
    error: error || statsError,
    filters,
    setFilters,
    updateFilter: (key: keyof DossierFilters, value: any) => {
      setFilters(prev => ({ ...prev, [key]: value }));
    },
    refetch
  };
};